/**
 * Band Gallery Data & Utilities
 *
 * Manages cigar band collection, gallery display, and community stats.
 */

import { SmokeLogEntry } from './personalization';

// Band entry - aggregated from smoke logs
export interface BandEntry {
  id: string;                    // Unique band ID (brand + line normalized)
  brand: string;
  line?: string;
  imageUrl: string;              // Primary/best image URL
  allImageUrls: string[];        // All uploaded images of this band
  logCount: number;              // Times this band has been logged (community)
  userLogCount: number;          // Times current user logged this band
  avgRating: number | null;      // Average rating across all logs
  firstLogged: string;           // ISO date
  lastLogged: string;            // ISO date
  contributors: number;          // Number of unique users who logged
}

// Band gallery filter options
export type BandSortOption = 'popular' | 'recent' | 'rating' | 'brand' | 'rare';
export type BandFilterOption = 'all' | 'my-collection' | 'trending' | 'rare';

// localStorage key for band gallery cache
const BAND_GALLERY_KEY = 'ring_ash_band_gallery';
const BAND_IMAGES_KEY = 'ring_ash_band_images';

/**
 * Generate a normalized band ID from brand and line
 */
export const generateBandId = (brand: string, line?: string): string => {
  const normalized = `${brand}${line ? `-${line}` : ''}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return normalized;
};

/**
 * Extract bands from smoke log entries
 */
export const extractBandsFromSmokeLog = (smokeLog: SmokeLogEntry[]): BandEntry[] => {
  const bandMap = new Map<string, BandEntry>();

  smokeLog.forEach(entry => {
    if (!entry.bandImageUrl) return;

    const bandId = generateBandId(entry.brand, entry.line);
    const existing = bandMap.get(bandId);

    if (existing) {
      // Update existing band entry
      existing.logCount++;
      existing.userLogCount++;
      if (!existing.allImageUrls.includes(entry.bandImageUrl)) {
        existing.allImageUrls.push(entry.bandImageUrl);
      }
      if (entry.rating) {
        const totalRating = (existing.avgRating || 0) * (existing.logCount - 1) + entry.rating;
        existing.avgRating = totalRating / existing.logCount;
      }
      if (entry.smokeDate > existing.lastLogged) {
        existing.lastLogged = entry.smokeDate;
        // Use most recent image as primary
        existing.imageUrl = entry.bandImageUrl;
      }
    } else {
      // Create new band entry
      bandMap.set(bandId, {
        id: bandId,
        brand: entry.brand,
        line: entry.line,
        imageUrl: entry.bandImageUrl,
        allImageUrls: [entry.bandImageUrl],
        logCount: 1,
        userLogCount: 1,
        avgRating: entry.rating || null,
        firstLogged: entry.smokeDate,
        lastLogged: entry.smokeDate,
        contributors: 1, // Will be updated with real data from API
      });
    }
  });

  return Array.from(bandMap.values());
};

/**
 * Sort bands by different criteria
 */
export const sortBands = (bands: BandEntry[], sortBy: BandSortOption): BandEntry[] => {
  const sorted = [...bands];

  switch (sortBy) {
    case 'popular':
      return sorted.sort((a, b) => b.logCount - a.logCount);
    case 'recent':
      return sorted.sort((a, b) =>
        new Date(b.lastLogged).getTime() - new Date(a.lastLogged).getTime()
      );
    case 'rating':
      return sorted.sort((a, b) => (b.avgRating || 0) - (a.avgRating || 0));
    case 'brand':
      return sorted.sort((a, b) => a.brand.localeCompare(b.brand));
    case 'rare':
      // Rare = low log count but has been logged
      return sorted.sort((a, b) => a.logCount - b.logCount);
    default:
      return sorted;
  }
};

/**
 * Filter bands
 */
export const filterBands = (
  bands: BandEntry[],
  filter: BandFilterOption,
  threshold: number = 5
): BandEntry[] => {
  switch (filter) {
    case 'my-collection':
      return bands.filter(b => b.userLogCount > 0);
    case 'trending':
      // Bands with high recent activity (logged in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return bands.filter(b =>
        new Date(b.lastLogged) >= thirtyDaysAgo && b.logCount >= 3
      );
    case 'rare':
      return bands.filter(b => b.logCount <= threshold && b.logCount > 0);
    case 'all':
    default:
      return bands;
  }
};

/**
 * Get band statistics
 */
export const getBandStats = (bands: BandEntry[]) => {
  const myBands = bands.filter(b => b.userLogCount > 0);
  const totalImages = bands.reduce((sum, b) => sum + b.allImageUrls.length, 0);
  const avgRating = bands.reduce((sum, b) => sum + (b.avgRating || 0), 0) / (bands.length || 1);

  // Find most logged brand
  const brandCounts = new Map<string, number>();
  bands.forEach(b => {
    brandCounts.set(b.brand, (brandCounts.get(b.brand) || 0) + b.logCount);
  });
  let topBrand = '';
  let topBrandCount = 0;
  brandCounts.forEach((count, brand) => {
    if (count > topBrandCount) {
      topBrand = brand;
      topBrandCount = count;
    }
  });

  return {
    totalBands: bands.length,
    myBandCount: myBands.length,
    totalImages,
    averageRating: Math.round(avgRating * 10) / 10,
    topBrand,
    topBrandCount,
    rareBandCount: bands.filter(b => b.logCount <= 3).length,
  };
};

/**
 * Get cached band gallery from localStorage
 */
export const getCachedBandGallery = (): BandEntry[] | null => {
  try {
    const cached = localStorage.getItem(BAND_GALLERY_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

/**
 * Cache band gallery to localStorage
 */
export const cacheBandGallery = (bands: BandEntry[]): void => {
  try {
    localStorage.setItem(BAND_GALLERY_KEY, JSON.stringify(bands));
  } catch (e) {
    console.warn('Failed to cache band gallery:', e);
  }
};

/**
 * Save band image reference locally (for guest mode)
 */
export const saveBandImageLocal = (
  smokeLogId: string,
  imageDataUrl: string
): string => {
  try {
    const images = JSON.parse(localStorage.getItem(BAND_IMAGES_KEY) || '{}');
    const imageId = `band-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    images[imageId] = {
      dataUrl: imageDataUrl,
      smokeLogId,
      createdAt: new Date().toISOString(),
    };
    localStorage.setItem(BAND_IMAGES_KEY, JSON.stringify(images));
    return imageId;
  } catch (e) {
    console.warn('Failed to save band image locally:', e);
    return '';
  }
};

/**
 * Get band image from local storage
 */
export const getBandImageLocal = (imageId: string): string | null => {
  try {
    const images = JSON.parse(localStorage.getItem(BAND_IMAGES_KEY) || '{}');
    return images[imageId]?.dataUrl || null;
  } catch {
    return null;
  }
};

/**
 * Get all local band images
 */
export const getAllLocalBandImages = (): Record<string, { dataUrl: string; smokeLogId: string; createdAt: string }> => {
  try {
    return JSON.parse(localStorage.getItem(BAND_IMAGES_KEY) || '{}');
  } catch {
    return {};
  }
};

// Featured/iconic bands for discovery (static data for empty states)
export const ICONIC_BANDS = [
  { brand: 'Arturo Fuente', line: 'Opus X', description: 'The legendary red and gold X - one of the most recognizable bands in the world' },
  { brand: 'Padrón', line: '1964 Anniversary', description: 'Classic elegance with the anniversary year stamp' },
  { brand: 'Drew Estate', line: 'Liga Privada No. 9', description: 'The distinctive LP crest on black matte' },
  { brand: 'Davidoff', line: 'Winston Churchill', description: 'Minimalist gold on white - Swiss precision' },
  { brand: 'My Father', line: 'Le Bijou 1922', description: 'Ornate vintage design honoring Don Pepin' },
  { brand: 'Oliva', line: 'Serie V Melanio', description: 'Bold red and gold with the Melanio portrait' },
  { brand: 'Ashton', line: 'VSG', description: 'Virgin Sun Grown - rich burgundy and gold' },
  { brand: 'Montecristo', line: 'No. 2', description: 'The classic Cuban heritage triangle' },
];

export default {
  generateBandId,
  extractBandsFromSmokeLog,
  sortBands,
  filterBands,
  getBandStats,
  getCachedBandGallery,
  cacheBandGallery,
  saveBandImageLocal,
  getBandImageLocal,
  getAllLocalBandImages,
  ICONIC_BANDS,
};
